import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertTitle, Typography } from "@mui/material";
import {
  Form,
  FormStep,
  FormSubmit,
  InputField,
  Link,
  PasswordField,
  ZxcvbnPasswordField,
  validatePassword,
} from "@synota-io/synota-shared-ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { ACCOUNT_LOGIN_PATH, HOME_PATH } from "../../../paths";
import { useAuth } from "../../../api/auth/useAuth";
import { useActivateUser } from "../../../api/hooks/useActivateUser";

const getSchema = (requiredScore?: number) =>
  yup
    .object({
      email: yup.string().required("User is required"),
      activation_code: yup.string().required("Code is required"),
      password: yup
        .string()
        .required("Password is required")
        .test("zxcvbn", "Password is too weak", (value, context) =>
          validatePassword(value, context.parent.email, requiredScore)
        ),
      password2: yup
        .string()
        .required("Please repeat your password")
        .oneOf([yup.ref("password"), ""], "Passwords must match"),
    })
    .required();

export const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const [hasSignedUp, setSignedUp] = useState(false);

  const hasCode = searchParams.has("code");
  const activation_code = searchParams.get("code") || "";
  const score = Number(searchParams.get("s") || "3");

  const { isLogged } = useAuth();
  const { activateUser, isPending, status } = useActivateUser();

  const form = useForm({
    defaultValues: {
      activation_code,
      email: "",
      password: "",
      password2: "",
    },
    resolver: yupResolver(getSchema(score)),
  });

  const onSubmit = form.handleSubmit(({ activation_code, email, password }) => {
    activateUser(
      { activation_code, email, password },
      {
        onError: () => {
          form.setError("root", {
            message: "There was an unexpected error, please try again",
          });
        },
        onSuccess({ data }) {
          if (!data.error) {
            return setSignedUp(true);
          }
          if (data.error_msg) {
            form.setError("root", { message: data.error_msg });
          }
        },
      }
    );
  });

  if (isLogged) {
    return <Navigate to={HOME_PATH} />;
  }

  if (status === "success" && hasSignedUp) {
    return (
      <Form>
        <Alert>
          <AlertTitle>
            Account set up successfully, click{" "}
            <Link to={ACCOUNT_LOGIN_PATH}>here</Link> to login.
          </AlertTitle>
        </Alert>
      </Form>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      <FormStep>
        <InputField
          fullWidth
          autoFocus={!hasCode}
          readOnly={hasCode}
          disabled={hasCode}
          control={form.control}
          autoComplete="off"
          name="activation_code"
          header="Activation Code"
        />

        <InputField
          autoFocus={hasCode}
          header="Email"
          fullWidth
          name="email"
          control={form.control}
        />

        <ZxcvbnPasswordField
          control={form.control}
          fullWidth
          name="password"
          header="Create a password"
        />

        <PasswordField
          header="Repeat password"
          fullWidth
          autoComplete="new-password"
          name="password2"
          control={form.control}
        />

        <FormSubmit isLoading={isPending} color="neutral">
          Signup
        </FormSubmit>
      </FormStep>
      {form.formState.errors.root && (
        <Typography color="error" variant="body1">
          {form.formState.errors.root.message}
        </Typography>
      )}
    </Form>
  );
};
